import { useEffect, useState } from "react";
import type { Appointment } from "../../types/garage/planning";
import type { OrdreStatus, OrdreIntervention } from "types/ordre";
import { isAPrevoir } from "types/ordre";
import { formatTime } from "@utils/timeGrid";
import { ordresApi } from "@services/api/ordres";
import { STATUS_CHIP_STYLES } from "./dropdown/OrdreStatusDropdown";

// Interventions + vehicle model live on the ordre, not on the lightweight
// appointment record, so the planning tooltip fetches them lazily on hover and
// caches by ordre_id to keep repeat hovers instant. Mirrors OrdreHoverPanel's
// "Interventions (N)" look.
const interventionsCache = new Map<string, OrdreIntervention[]>();
const modeleCache = new Map<string, string>();

/**
 * Black hover box shown over a planning appointment.
 * Displays client / plate / dates and, when the appointment is linked to an
 * ordre, a clickable status chip that opens the status menu (via onEditStatus).
 *
 * Rendered as a direct child of the appointment cell: positioning reads
 * `el.parentElement` to anchor itself beside that cell.
 */
export default function AppointmentTooltip({
  appt,
  appointments,
  status,
  fallbackDate,
  onEditStatus,
  onMouseEnter,
  onMouseLeave,
}: {
  appt: Appointment;
  appointments: Appointment[];
  status?: OrdreStatus;
  fallbackDate?: string;
  onEditStatus: (anchor: DOMRect) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const thisDate = appt.date || fallbackDate || "";
  const fmtDate = (d: string) =>
    new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

  // Lazily load the linked ordre's interventions + vehicle model (null = not
  // loaded yet). Both come from the same ordre fetch.
  const oid = appt.ordre_id;
  const [interventions, setInterventions] = useState<OrdreIntervention[] | null>(
    () => (oid ? interventionsCache.get(oid) ?? null : null),
  );
  const [modele, setModele] = useState<string | null>(
    () => (oid ? modeleCache.get(oid) ?? null : null),
  );
  useEffect(() => {
    if (!oid || interventionsCache.has(oid)) return;
    let alive = true;
    ordresApi
      .get(oid)
      .then((ordre) => {
        const list = ordre.interventions || [];
        const veh = (ordre.modele || "").trim();
        interventionsCache.set(oid, list);
        modeleCache.set(oid, veh);
        if (alive) {
          setInterventions(list);
          setModele(veh);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [oid]);

  const pair = appt.ordre_id ? appointments.find((a) => a.ordre_id === appt.ordre_id && a.id !== appt.id) : null;
  const receptionAppt = appt.type === "reception" ? appt : pair;
  const restitutionAppt = appt.type === "restitution" ? appt : pair;

  const chip = status ? STATUS_CHIP_STYLES[status] : null;

  return (
    <div
      data-appt-tooltip
      className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2"
      style={{ whiteSpace: "nowrap", minWidth: 140, maxWidth: 320 }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={(el) => {
        if (!el) return;
        const parent = el.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const tipW = el.offsetWidth;
        const tipH = el.offsetHeight;
        // Keep a comfortable gap from every screen edge — the box must never hug
        // or overflow a border (esp. a tall interventions list near the bottom).
        const M = 16;
        // Place the tooltip flush against the event (1px overlap) instead of with a
        // gap: an 8px gap can sit over a neighbouring event, so moving the pointer
        // toward the tooltip would land on that event and steal/hide the hover.
        const fitsRight = rect.right + tipW <= window.innerWidth - M;
        let left = fitsRight ? rect.right - 1 : rect.left - tipW + 1;
        left = Math.min(Math.max(left, M), window.innerWidth - tipW - M);
        let top = Math.min(rect.top, window.innerHeight - tipH - M);
        top = Math.max(top, M);
        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
      }}
    >
      <div className="text-h4 font-medium">{appt.clientName}</div>
      {modele && <div className="text-h5 text-neutral-200">{modele}</div>}
      {appt.plate && <div className="text-h5 text-neutral-300">{appt.plate}</div>}
      {appt.num && <div className="text-h5 text-neutral-300">OR n°{appt.num}</div>}

      {receptionAppt && (
        <div className="text-h5 text-neutral-300">
          Réception : {fmtDate(receptionAppt.date || thisDate)} {formatTime(receptionAppt.startHour)} — {formatTime(receptionAppt.startHour + receptionAppt.durationHours)}
        </div>
      )}
      {restitutionAppt && (
        <div className="text-h5 text-neutral-300">
          Restitution : {fmtDate(restitutionAppt.date || thisDate)} {formatTime(restitutionAppt.startHour)} — {formatTime(restitutionAppt.startHour + restitutionAppt.durationHours)}
        </div>
      )}
      {!receptionAppt && !restitutionAppt && (
        <div className="text-h5 text-neutral-300">
          {fmtDate(thisDate)} {formatTime(appt.startHour)} — {formatTime(appt.startHour + appt.durationHours)}
        </div>
      )}

      {oid && interventions !== null && (
        <div className="mt-2 pt-2 border-t border-neutral-700" style={{ whiteSpace: "normal" }}>
          <div className="text-h5 text-neutral-400 font-medium mb-1">
            Interventions ({interventions.length})
          </div>
          {interventions.length === 0 ? (
            <div className="text-h5 text-neutral-500 italic">Aucune intervention</div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {interventions.slice(0, 8).map((it) => {
                const aPrevoir = isAPrevoir(it);
                return (
                  <div key={it.id} className="flex flex-row items-baseline justify-between gap-2">
                    <span className={`text-h5 truncate ${aPrevoir ? "text-amber-300/80 line-through decoration-amber-400/50" : "text-white"}`}>
                      {it.label}{aPrevoir ? " · à prévoir" : ""}
                    </span>
                    {it.estimated_hours ? (
                      <span className="text-h5 text-neutral-400 flex-shrink-0">{it.estimated_hours}h</span>
                    ) : null}
                  </div>
                );
              })}
              {interventions.length > 8 && (
                <div className="text-h5 text-neutral-500">+{interventions.length - 8} autre(s)</div>
              )}
            </div>
          )}
        </div>
      )}

      {chip && status ? (
        <div className="flex flex-col gap-1 mt-2">
          <span className="text-h5 text-neutral-400">Statut de l'OR</span>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onEditStatus((e.currentTarget as HTMLElement).getBoundingClientRect());
            }}
            className="flex flex-row items-center gap-1.5 font-medium cursor-pointer transition-opacity hover:opacity-80 w-fit"
            style={{ background: chip.bg, color: chip.color, borderRadius: 5, padding: "3px 10px", fontSize: 12, lineHeight: "16px" }}
          >
            {chip.label}
            <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
              <path d="M1 1l3 3 3-3" stroke={chip.color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="text-h5 text-neutral-400 mt-1">Cliquer pour ouvrir</div>
      )}
    </div>
  );
}
